import type { CaptureParse } from '@hugo/core'
import { ArrowDownIcon, ArrowUpIcon, CalendarIcon, ListTodoIcon, UserIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { formatDue, kindLabel } from '@/lib/format'
import { log } from '@/lib/log'
import { priorityLabel } from '@/lib/priority'
import { priorityTone } from '@/lib/styles'
import { tagLabel } from '@/lib/tags'
import { callTool } from '@/lib/tools'

/** Live preview of what a capture will create (server-side parse, debounced). */
export function useCapturePreview(text: string) {
  const [preview, setPreview] = useState<CaptureParse | null>(null)
  useEffect(() => {
    const trimmed = text.trim()
    if (!trimmed) {
      setPreview(null)
      return
    }
    let cancelled = false
    const timer = setTimeout(() => {
      callTool('capture.parse', { text: trimmed })
        .then(({ result }) => {
          if (!cancelled) setPreview(result)
        })
        .catch((error: unknown) => log.warn('capture preview failed', String(error)))
    }, 150)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [text])
  return preview
}

/**
 * What a capture will create: the list, the person (new or known), the due date and the title.
 * `kind` overrides the parsed list (on the board, the column decides).
 */
export function CapturePreview({
  preview,
  timeZone,
  kind,
  showTitle = true,
}: {
  preview: CaptureParse
  timeZone: string
  kind?: CaptureParse['kind']
  showTitle?: boolean
}) {
  return (
    <>
      <Badge variant="secondary">
        <ListTodoIcon aria-hidden="true" />
        {kindLabel(kind ?? preview.kind)}
      </Badge>
      {preview.counterpartName ? (
        <Badge variant="outline">
          <UserIcon aria-hidden="true" />
          {preview.counterpartName}
          {preview.counterpartKnown ? '' : ' (new)'}
        </Badge>
      ) : null}
      {preview.due ? (
        <Badge variant="outline">
          <CalendarIcon aria-hidden="true" />
          {formatDue(preview.due.at, preview.due.allDay, timeZone)}
        </Badge>
      ) : null}
      {preview.priority ? (
        <Badge
          variant="outline"
          className={preview.priority === 'high' ? priorityTone.high : priorityTone.low}
          data-testid="capture-priority"
        >
          {preview.priority === 'high' ? (
            <ArrowUpIcon aria-hidden="true" />
          ) : (
            <ArrowDownIcon aria-hidden="true" />
          )}
          {priorityLabel(preview.priority)}
        </Badge>
      ) : null}
      {preview.tags.map((tag) => (
        <Badge key={tag} variant="outline" data-testid="capture-tag">
          {tagLabel(tag)}
        </Badge>
      ))}
      {showTitle ? <span className="truncate text-muted-foreground">“{preview.title}”</span> : null}
    </>
  )
}
